import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, Mail, ChevronRight, CheckCircle, Circle, Heart, Tag, MessageCircle, Lock } from 'react-feather';
import { XIcon } from './XIcon';
import BulkEmailCoachModal from './BulkEmailCoachModal';
import BulkTweetCoachModal from './BulkTweetCoachModal';
import SendDMPicker from './SendDMPicker';
import { fetchDmTargets, DMTargets } from '../services/dmTargetsAPI';
import { useResponsive } from '../hooks/useResponsive';
import { customListsAPI } from '../services/customListsAPI';
import { coachUnlocksAPI } from '../services/api';
import { useSubscriptionGuard } from '../hooks/useSubscriptionGuard';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../hooks/useConfirm';
import {
  UnlockState,
  confirmCopy,
  costsProgram,
  fitSelection,
  joinNames,
  newPrograms,
  unlockStateFrom,
} from './gameplan/emailPrograms';
import {
  PROGRAMS_STATUS_FAILED,
  PROGRAMS_SWAP_TOAST,
  programsBlockedToast,
} from './gameplan/gamePlanCopy';
import {
  canPickNewSchool,
  contactShown,
  fetchPlan,
  offeredCoaches,
  rowNote,
  shownPicks,
  toProgramRows,
  withHouseSchools,
} from './bulkMessagingPrograms';

// Type definitions matching CollegeLists
interface CoachingStaffMember {
  production_id?: number | string;
  coach_name: string;
  position?: string;
  title?: string;
  email?: string;
  has_email?: boolean;
  has_twitter_handle?: boolean;
  phone?: string;
  twitter_handle?: string;
  instagram_handle?: string;
  bio_summary?: string;
  source_type?: string;
  approved_by?: string;
  approved_at?: string;
  created_at?: string;
  updated_at?: string;
}

interface School {
  school_id: number;
  name: string;
  coaching_staff?: CoachingStaffMember[];
  category?: 'dream' | 'target' | 'safety';
  logo_url?: string | null;
}

interface CustomListBasic {
  id: number;
  name: string;
  color: string;
  school_count: number;
}

interface BulkMessagingModalProps {
  isOpen: boolean;
  onClose: () => void;
  schools: School[];
  athleteId: string;
  athleteName?: string;
  customLists?: CustomListBasic[];
  schoolMemberships?: { [schoolId: number]: number[] };
  sessionToken?: string;
  /** Called on close when this visit used a free program, so the page can reload its lists. */
  onProgramsUnlocked?: () => void;
}

type MessagingType = 'email' | 'tweet' | 'dm';
type StatusLoad = 'idle' | 'loading' | 'ready' | 'error';

const NO_PROGRAMS: UnlockState = { isPaid: false, remaining: 0, unlocked: new Set() };
type Step = 'type' | 'schools' | 'coaches' | 'dm';
type ListFilter = 'all' | 'dream' | 'target' | 'safety' | number;

const BulkMessagingModal: React.FC<BulkMessagingModalProps> = ({
  isOpen,
  onClose,
  schools,
  athleteId,
  athleteName = 'Athlete',
  customLists = [],
  schoolMemberships = {},
  sessionToken = '',
  onProgramsUnlocked,
}) => {
  const { isMobile } = useResponsive();
  const { isPaid, requirePaid } = useSubscriptionGuard();
  const toast = useToast();
  // Free programs (§1.5). Each NEW school a free family reaches uses one of its
  // 3 free programs. The list hides coach contacts until a program is held, so
  // the school step counts contacts on file and unlocks after a confirm.
  const [gating, setGating] = useState(false);
  const [unlockState, setUnlockState] = useState<UnlockState | null>(null);
  const [statusLoad, setStatusLoad] = useState<StatusLoad>('idle');
  const [statusKey, setStatusKey] = useState(0);
  // Staff the unlock call sent back, by school. Replaces the hidden list staff.
  const [revealed, setRevealed] = useState<Map<number, CoachingStaffMember[]>>(new Map());
  const [programError, setProgramError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const spentProgram = useRef(false);
  const [messagingType, setMessagingType] = useState<MessagingType | null>(null);
  const [selectedSchools, setSelectedSchools] = useState<Set<number>>(new Set());
  const [selectedCoaches, setSelectedCoaches] = useState<Set<string>>(new Set());
  const [currentStep, setCurrentStep] = useState<Step>('type');
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [showBulkTweetModal, setShowBulkTweetModal] = useState(false);
  const [listFilter, setListFilter] = useState<ListFilter>('all');
  const [customListOnlySchools, setCustomListOnlySchools] = useState<School[]>([]);
  const [isLoadingCustomSchools, setIsLoadingCustomSchools] = useState(false);
  // "Send a DM" (APS-253) shows only once the server answers with dm_targets.
  const [dmTargets, setDmTargets] = useState<DMTargets | null>(null);

  useEffect(() => {
    if (!isOpen || !sessionToken || !athleteId) return;
    let cancelled = false;
    setDmTargets(null);
    fetchDmTargets(sessionToken, athleteId).then((t) => {
      if (!cancelled) setDmTargets(t);
    });
    return () => { cancelled = true; };
  }, [isOpen, sessionToken, athleteId]);

  // When modal opens, load custom-list-only schools (ones not in heart categories)
  useEffect(() => {
    if (!isOpen || customLists.length === 0 || !sessionToken || !athleteId) return;

    const heartSchoolIds = new Set(schools.map(s => s.school_id));
    // Check if any custom list schools are NOT in heart categories
    const hasCustomOnlySchools = Object.keys(schoolMemberships).some(
      id => !heartSchoolIds.has(Number(id))
    );

    if (hasCustomOnlySchools) {
      setIsLoadingCustomSchools(true);
      customListsAPI.getAllCustomListSchools(sessionToken, athleteId)
        .then(res => {
          if (res.success) {
            const customOnly = res.data.schools
              .filter(s => !heartSchoolIds.has(s.school_id))
              .map(s => ({
                school_id: s.school_id,
                name: s.name,
                logo_url: s.logo_url,
                coaching_staff: s.coaching_staff,
                category: undefined,
              }));
            setCustomListOnlySchools(customOnly);
          }
        })
        .catch(err => console.error('Error loading custom list schools:', err))
        .finally(() => setIsLoadingCustomSchools(false));
    }
  }, [isOpen]);

  // All schools = heart schools + custom-list-only schools (deduped)
  const allSchools = useMemo(() => {
    const heartIds = new Set(schools.map(s => s.school_id));
    const merged = [...schools];
    customListOnlySchools.forEach(s => {
      if (!heartIds.has(s.school_id)) merged.push(s);
    });
    return merged;
  }, [schools, customListOnlySchools]);

  // A paid family, or a free one the server says is paid, skips free programs.
  const paidPath = isPaid || unlockState?.isPaid === true;
  // House schools never use a program, so the picker treats them as held.
  const programState = useMemo(
    () => (unlockState ? withHouseSchools(unlockState) : null),
    [unlockState],
  );
  const channel = messagingType === 'email' || messagingType === 'tweet' ? messagingType : null;

  // Read the free programs each time the school step opens.
  useEffect(() => {
    if (!isOpen || currentStep !== 'schools') return undefined;
    if (isPaid) {
      setUnlockState({ isPaid: true, remaining: 0, unlocked: new Set() });
      setStatusLoad('ready');
      return undefined;
    }
    if (!sessionToken || !athleteId) {
      setStatusLoad('error');
      return undefined;
    }
    let alive = true;
    setStatusLoad('loading');
    coachUnlocksAPI
      .getStatus(sessionToken, athleteId)
      .then((s) => unlockStateFrom(s))
      .catch(() => null)
      .then((st) => {
        if (!alive) return;
        if (!st) {
          setStatusLoad('error');
          return;
        }
        setUnlockState(st);
        setStatusLoad('ready');
      });
    return () => { alive = false; };
  }, [isOpen, currentStep, isPaid, sessionToken, athleteId, statusKey]);

  const staffOf = (school: School): CoachingStaffMember[] =>
    revealed.get(school.school_id) || school.coaching_staff || [];

  const withStaff = (school: School): School => ({ ...school, coaching_staff: staffOf(school) });

  // Return focus to the button that opened this modal once it closes.
  const openerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    openerRef.current = document.activeElement as HTMLElement | null;
    return () => {
      const opener = openerRef.current;
      openerRef.current = null;
      if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus();
    };
  }, [isOpen]);

  const handleClose = () => {
    setMessagingType(null);
    setSelectedSchools(new Set());
    setSelectedCoaches(new Set());
    setCurrentStep('type');
    setShowBulkEmailModal(false);
    setShowBulkTweetModal(false);
    setListFilter('all');
    setGating(false);
    setUnlockState(null);
    setStatusLoad('idle');
    setRevealed(new Map());
    setProgramError(null);
    const spent = spentProgram.current;
    spentProgram.current = false;
    onClose();
    if (spent && onProgramsUnlocked) onProgramsUnlocked();
  };

  const handleMessagingTypeSelect = (type: MessagingType) => {
    // Picks belong to one channel. A school picked for email may have no
    // coach on X, so a new channel starts with nothing picked.
    if (type !== messagingType) {
      setSelectedSchools(new Set());
      setSelectedCoaches(new Set());
    }
    setMessagingType(type);
    setProgramError(null);
    setCurrentStep(type === 'dm' ? 'dm' : 'schools');
  };

  const showUpgrade = (schoolName?: string) => {
    const verb = messagingType === 'tweet' ? 'Tag coaches' : 'Email coaches';
    requirePaid(schoolName ? `${verb} at ${schoolName}` : `${verb} at more schools`, () => {});
  };

  const toggleSchoolSelection = (schoolId: number) => {
    if (gating) return;
    const newSelected = new Set(selectedSchools);
    if (newSelected.has(schoolId)) {
      newSelected.delete(schoolId);
      // Remove coaches from deselected schools
      const school = allSchools.find(s => s.school_id === schoolId);
      if (school) {
        const newSelectedCoaches = new Set(selectedCoaches);
        staffOf(school).forEach(coach => {
          const coachKey = `${schoolId}-${coach.coach_name}`;
          newSelectedCoaches.delete(coachKey);
        });
        setSelectedCoaches(newSelectedCoaches);
      }
    } else {
      if (!paidPath) {
        // Free: wait for the program count, and stop at the free programs left.
        if (!programState) return;
        if (costsProgram(schoolId, programState) && !canPickNewSchool(selectedSchools, programState)) {
          const school = allSchools.find(s => s.school_id === schoolId);
          if (programState.remaining <= 0) showUpgrade(school?.name);
          else toast.info(PROGRAMS_SWAP_TOAST);
          return;
        }
      }
      newSelected.add(schoolId);
    }
    setProgramError(null);
    setSelectedSchools(newSelected);
  };

  const toggleCoachSelection = (schoolId: number, coachName: string) => {
    const coachKey = `${schoolId}-${coachName}`;
    const newSelected = new Set(selectedCoaches);
    if (newSelected.has(coachKey)) {
      newSelected.delete(coachKey);
    } else {
      newSelected.add(coachKey);
    }
    setSelectedCoaches(newSelected);
  };

  const getSelectedSchoolsList = () => {
    return allSchools.filter(school => selectedSchools.has(school.school_id));
  };

  const getFilteredSchools = () => {
    if (!messagingType) return allSchools;

    // Start with all schools, then apply list filter
    let pool = allSchools;

    if (listFilter === 'dream') {
      pool = pool.filter(s => s.category === 'dream');
    } else if (listFilter === 'target') {
      pool = pool.filter(s => s.category === 'target');
    } else if (listFilter === 'safety') {
      pool = pool.filter(s => s.category === 'safety');
    } else if (typeof listFilter === 'number') {
      // Custom list filter
      pool = pool.filter(s => (schoolMemberships[s.school_id] || []).includes(listFilter));
    }

    if (!channel) return [];
    // A free family sees every school with the contact on file, shown or not.
    return pool.filter(school => offeredCoaches(withStaff(school), channel, paidPath).length > 0);
  };

  // The picker rows in the shape the free-program helpers take.
  const currentRows = () =>
    channel ? toProgramRows(getFilteredSchools().map(withStaff), channel, paidPath) : [];

  // What Select All picks. A free family gets every held school, then new
  // ones in list order up to the free programs left.
  const selectAllPick = (): Set<number> => {
    const ids = new Set(getFilteredSchools().map(s => s.school_id));
    if (paidPath) return ids;
    if (!programState) return new Set();
    return fitSelection(ids, currentRows(), programState);
  };

  const allPicked = () => {
    if (paidPath) return selectedSchools.size === getFilteredSchools().length;
    const pick = selectAllPick();
    return selectedSchools.size > 0 && Array.from(pick).every(id => selectedSchools.has(id));
  };

  const getFilteredCoaches = (school: School) => {
    if (!channel) return [];
    return staffOf(school).filter(coach => contactShown(coach, channel));
  };

  // Helper function to render heart category pills
  const renderHeartCategoryPill = (category?: 'dream' | 'target' | 'safety') => {
    if (!category) return null;

    const getHeartConfig = () => {
      switch (category) {
        case 'dream':
          return {
            hearts: [true, true, true],
            color: '#10B981' // Green (Dream - 3 hearts)
          };
        case 'target':
          return {
            hearts: [false, true, true],
            color: '#F59E0B'
          };
        case 'safety':
          return {
            hearts: [false, false, true],
            color: '#DC2626' // Red (Safety - 1 heart)
          };
        default:
          return {
            hearts: [false, false, false],
            color: '#E5E7EB'
          };
      }
    };

    const config = getHeartConfig();

    return (
      <div style={{ display: 'flex', gap: '2px' }}>
        {config.hearts.map((filled, index) => (
          <Heart 
            key={index}
            size={12} 
            color={filled ? config.color : '#E5E7EB'}
            fill={filled ? config.color : 'none'}
          />
        ))}
      </div>
    );
  };

  const handleRemoveCoach = (coachEmail: string) => {
    // Find the coach key that matches this email
    const coachKeyToRemove = Array.from(selectedCoaches).find(coachKey => {
      // Split on the FIRST hyphen only: schoolId is numeric, but coach names can
      // contain hyphens ("John Smith-Jones"). split('-') dropped the remainder,
      // silently omitting hyphenated-name coaches from sends (#191).
      const dashIdx = coachKey.indexOf('-');
      const schoolIdStr = coachKey.slice(0, dashIdx);
      const coachName = coachKey.slice(dashIdx + 1);
      const schoolId = parseInt(schoolIdStr, 10);

      const school = allSchools.find(s => s.school_id === schoolId);
      if (school) {
        const coach = staffOf(school).find(c => c.coach_name === coachName);
        return coach && coach.email === coachEmail;
      }
      return false;
    });

    if (coachKeyToRemove) {
      const newSelected = new Set(selectedCoaches);
      newSelected.delete(coachKeyToRemove);
      setSelectedCoaches(newSelected);
    }
  };

  const getSelectedCoachesData = (forTweet: boolean = false) => {
    const selectedCoachesData: Array<{
      coach_name: string;
      email?: string;
      title?: string;
      school?: string;
      twitter_handle?: string;
      schoolLogo?: string;
      coach_id?: string;
      school_id?: string;
    }> = [];

    Array.from(selectedCoaches).forEach(coachKey => {
      // First-hyphen split so hyphenated coach names aren't dropped (#191).
      const dashIdx = coachKey.indexOf('-');
      const schoolIdStr = coachKey.slice(0, dashIdx);
      const coachName = coachKey.slice(dashIdx + 1);
      const schoolId = parseInt(schoolIdStr, 10);
      const school = allSchools.find(s => s.school_id === schoolId);
      
      if (school) {
        const coach = staffOf(school).find(c => c.coach_name === coachName);
        if (coach && contactShown(coach, forTweet ? 'tweet' : 'email')) {
          selectedCoachesData.push({
            coach_name: coach.coach_name,
            email: coach.email,
            title: coach.title,
            school: school.name,
            twitter_handle: coach.twitter_handle,
            schoolLogo: school.logo_url || undefined,
            coach_id: coach.production_id?.toString(),
            school_id: school.school_id?.toString()
          });
        }
      }
    });

    return selectedCoachesData;
  };

  // Every coach with a shown contact at the given schools.
  const coachKeysFor = (schoolIds: Set<number>, staffBySchool: Map<number, CoachingStaffMember[]>) => {
    const keys = new Set<string>();
    if (!channel) return keys;
    schoolIds.forEach(schoolId => {
      const school = allSchools.find(s => s.school_id === schoolId);
      if (!school) return;
      const staff = staffBySchool.get(schoolId) || school.coaching_staff || [];
      staff.forEach(coach => {
        if (contactShown(coach, channel)) keys.add(`${school.school_id}-${coach.coach_name}`);
      });
    });
    return keys;
  };

  const openComposer = () => {
    if (messagingType === 'email') {
      setShowBulkEmailModal(true);
    } else if (messagingType === 'tweet') {
      setShowBulkTweetModal(true);
    }
  };

  const freshPrograms = useMemo(
    () => (programState && channel
      ? newPrograms(selectedSchools, currentRows(), programState)
      : []),
    // currentRows reads the same inputs listed here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedSchools, programState, channel, allSchools, revealed, listFilter, schoolMemberships, paidPath],
  );
  const spendText = confirmCopy(freshPrograms, programState ? programState.remaining : 0);
  const { ConfirmComponent: SpendConfirm, confirm: confirmSpend } = useConfirm({
    title: spendText.title,
    message: spendText.message,
    confirmText: 'Unlock',
    cancelText: 'Cancel',
    variant: 'info',
  });

  /**
   * Free family, school step done. Names the new programs in a confirm, then
   * unlocks one school at a time. Schools already held cost nothing. A 402
   * keeps every school gathered so far.
   */
  const unlockAndContinue = async () => {
    if (inFlight.current || !channel || !programState || !sessionToken || !athleteId) return;
    inFlight.current = true;
    setProgramError(null);
    const picks = shownPicks(selectedSchools, getFilteredSchools().map(s => s.school_id));
    const rows = currentRows();
    const pickedSchools = getFilteredSchools().filter(s => picks.has(s.school_id));
    let cur = programState;
    let raw: UnlockState = unlockState || NO_PROGRAMS;
    try {
      if (newPrograms(picks, rows, cur).length > 0) {
        // Read the free count again so the confirm is current.
        setGating(true);
        const st = unlockStateFrom(await coachUnlocksAPI.getStatus(sessionToken, athleteId).catch(() => null));
        setGating(false);
        if (!st) {
          setProgramError(PROGRAMS_STATUS_FAILED);
          return;
        }
        raw = st;
        cur = withHouseSchools(st);
        setUnlockState(st);
        const fitted = fitSelection(picks, rows, cur);
        if (fitted.size !== picks.size) {
          setSelectedSchools(fitted);
          setProgramError('Your free programs changed. Check your picks.');
          return;
        }
        if (newPrograms(picks, rows, cur).length > 0) {
          const ok = await confirmSpend();
          if (!ok) return;
        }
      }

      setGating(true);
      const staffBySchool = new Map(revealed);
      const unlockedNow = new Set(raw.unlocked);
      let remaining = raw.remaining;
      const blocked: School[] = [];
      const failed: School[] = [];
      for (const school of pickedSchools) {
        if (fetchPlan(withStaff(school), channel, cur) === 'listed') continue;
        const costs = costsProgram(school.school_id, cur);
        if (costs && blocked.length > 0) {
          blocked.push(school);
          continue;
        }
        const res = await coachUnlocksAPI.unlockSchool(sessionToken, school.school_id, athleteId).catch(() => null);
        if (res && (res.status === 402 || res.paywall)) {
          blocked.push(school);
          remaining = 0;
          continue;
        }
        if (!res || res.status !== 200 || !res.unlocked) {
          failed.push(school);
          continue;
        }
        if (costs && !cur.isPaid) spentProgram.current = true;
        if (!cur.isPaid) unlockedNow.add(String(school.school_id));
        if (typeof res.remaining === 'number') remaining = res.remaining;
        staffBySchool.set(school.school_id, (res.coaches || []).map(c => ({
          ...c,
          coach_name: (c.coach_name || '').trim() || 'Coach',
          email: c.email || undefined,
          twitter_handle: c.twitter_handle || undefined,
        })));
      }

      setRevealed(staffBySchool);
      if (!cur.isPaid) setUnlockState({ isPaid: false, remaining: Math.max(0, remaining), unlocked: unlockedNow });

      const dropped = new Set([...blocked, ...failed].map(s => s.school_id));
      const kept = new Set(Array.from(picks).filter(id => !dropped.has(id)));
      const keys = coachKeysFor(kept, staffBySchool);
      setSelectedSchools(kept);

      if (keys.size === 0) {
        setSelectedCoaches(new Set());
        if (blocked.length > 0) showUpgrade(blocked[0].name);
        else if (failed.length > 0) setProgramError('We could not open these schools. Try again.');
        else setProgramError(channel === 'email'
          ? 'We have no coach emails on file for these schools yet.'
          : 'We have no coach X handles on file for these schools yet.');
        return;
      }
      if (blocked.length > 0) toast.warning(programsBlockedToast(blocked.map(s => s.name)));
      if (failed.length > 0) toast.warning(`We could not open ${joinNames(failed.map(s => s.name))}. The rest are ready.`);
      setSelectedCoaches(keys);
      setCurrentStep('coaches');
    } finally {
      inFlight.current = false;
      setGating(false);
    }
  };

  const handleProceed = async () => {
    if (currentStep === 'schools' && selectedSchools.size > 0) {
      if (paidPath) {
        setCurrentStep('coaches');
        setSelectedCoaches(coachKeysFor(selectedSchools, revealed));
        return;
      }
      await unlockAndContinue();
      return;
    }
    if (currentStep !== 'coaches' || selectedCoaches.size === 0) return;
    // Programs were settled on the school step, so the composer opens now.
    openComposer();
  };

  const canProceed = () => {
    if (currentStep === 'schools') {
      if (!paidPath && statusLoad !== 'ready') return false;
      return selectedSchools.size > 0;
    }
    if (currentStep === 'coaches') return selectedCoaches.size > 0;
    return false;
  };

  const handleBulkEmailClose = () => {
    setShowBulkEmailModal(false);
  };

  const handleBulkTweetClose = () => {
    setShowBulkTweetModal(false);
  };

  // Values the school step reads on each render.
  const pickerSchools = currentStep === 'schools' ? getFilteredSchools() : [];
  const pickerRows = currentStep === 'schools' ? currentRows() : [];
  const canPickMore = programState ? canPickNewSchool(selectedSchools, programState) : false;
  const outOfPrograms = !!programState && !programState.isPaid && programState.remaining <= 0;
  const hasNewSchool = !!programState && pickerRows.some(r => costsProgram(r.school_id, programState));

  if (!isOpen) return null;

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px',
        }}
        onClick={handleClose}
      >
        <div
          style={{
            width: isMobile ? 'calc(100vw - 32px)' : '100%',
            maxWidth: isMobile ? 'calc(100vw - 32px)' : '700px',
            maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
            background: 'white',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              padding: isMobile ? '16px' : '24px 32px',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <h2
              style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#1F2937',
                margin: 0,
              }}
            >
              Send a Message
            </h2>
            <button
              onClick={handleClose}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                color: '#6B7280',
              }}
            >
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '32px',
              background: '#F9FAFB',
            }}
          >
            {/* Type Selection Step */}
            {currentStep === 'type' && (
              <div>
                <h3
                  style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#1F2937',
                    marginBottom: '20px',
                  }}
                >
                  Choose messaging method:
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {/* Email Button */}
                  <button
                    onClick={() => handleMessagingTypeSelect('email')}
                    style={{
                      background: 'white',
                      padding: '20px',
                      borderRadius: '12px',
                      border: '1px solid #E5E7EB',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#F9550D';
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(249, 85, 13, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#E5E7EB';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        background: 'rgba(234, 67, 53, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Mail size={28} color="#EA4335" />
                    </div>
                    <div style={{ flex: 1, textAlign: 'left' }}>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', marginBottom: '4px' }}>
                        Email Coaches
                      </div>
                      <div style={{ fontSize: '14px', color: '#6B7280' }}>
                        Send personalized emails to selected coaches
                      </div>
                    </div>
                    <ChevronRight size={20} color="#9CA3AF" />
                  </button>

                  {/* Tweet Button */}
                  <button
                    onClick={() => handleMessagingTypeSelect('tweet')}
                    style={{
                      background: 'white',
                      padding: '20px',
                      borderRadius: '12px',
                      border: '1px solid #E5E7EB',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#F9550D';
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(249, 85, 13, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#E5E7EB';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        background: 'rgba(0, 0, 0, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <XIcon size={28} color="#000000" />
                    </div>
                    <div style={{ flex: 1, textAlign: 'left' }}>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', marginBottom: '4px' }}>
                        Tag on X
                      </div>
                      <div style={{ fontSize: '14px', color: '#6B7280' }}>
                        Create tweets mentioning selected coaches
                      </div>
                    </div>
                    <ChevronRight size={20} color="#9CA3AF" />
                  </button>

                  {/* Send a DM (APS-253): one coach, the existing DM window. */}
                  {dmTargets && (
                    <button
                      onClick={() => handleMessagingTypeSelect('dm')}
                      style={{
                        background: 'white',
                        padding: '20px',
                        borderRadius: '12px',
                        border: '1px solid #E5E7EB',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#F9550D';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(249, 85, 13, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#E5E7EB';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '12px',
                          background: 'rgba(0, 0, 0, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <MessageCircle size={28} color="#000000" />
                      </div>
                      <div style={{ flex: 1, textAlign: 'left' }}>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', marginBottom: '4px' }}>
                          Send a DM
                        </div>
                        <div style={{ fontSize: '14px', color: '#6B7280' }}>
                          Direct message one coach on X
                        </div>
                      </div>
                      <ChevronRight size={20} color="#9CA3AF" />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Send a DM step: school, then one coach, then the DM window */}
            {currentStep === 'dm' && dmTargets && (
              <SendDMPicker
                athleteId={athleteId}
                targets={dmTargets}
                onExit={() => {
                  setMessagingType(null);
                  setCurrentStep('type');
                }}
                onSent={handleClose}
              />
            )}

            {/* Schools Selection Step */}
            {currentStep === 'schools' && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <h3
                    style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#1F2937',
                      margin: 0,
                    }}
                  >
                    Select schools ({selectedSchools.size} selected):
                  </h3>
                  <button
                    onClick={() => {
                      if (gating) return;
                      setProgramError(null);
                      if (allPicked()) {
                        // Deselect all
                        setSelectedSchools(new Set());
                        setSelectedCoaches(new Set());
                        return;
                      }
                      // Select all. A free family gets what its programs cover.
                      const pick = selectAllPick();
                      if (!paidPath && pick.size === 0) {
                        if (programState && programState.remaining <= 0) showUpgrade();
                        return;
                      }
                      setSelectedSchools(pick);
                    }}
                    style={{
                      background: 'transparent',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#6B7280',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#F9FAFB';
                      e.currentTarget.style.borderColor = '#F9550D';
                      e.currentTarget.style.color = '#F9550D';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.borderColor = '#D1D5DB';
                      e.currentTarget.style.color = '#6B7280';
                    }}
                  >
                    {allPicked() ? 'Deselect All' : 'Select All'}
                  </button>
                </div>

                {/* List Filter Chips */}
                {customLists.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                    {([
                      { key: 'all' as ListFilter, label: 'All', color: '#6B7280' },
                      // HOUSE RULE (#214): the heart-bucket chips show 3/2/1 hearts,
                      // never the words Dream/Target/Safety. Labels below are only
                      // used for the accessible name, not shown as chip text.
                      { key: 'dream' as ListFilter, label: '3 hearts', color: '#10B981' },
                      { key: 'target' as ListFilter, label: '2 hearts', color: '#F59E0B' },
                      { key: 'safety' as ListFilter, label: '1 heart', color: '#DC2626' },
                      ...customLists.map(cl => ({ key: cl.id as ListFilter, label: cl.name, color: cl.color })),
                    ] as Array<{ key: ListFilter; label: string; color: string }>).map(chip => {
                      const isHeartBucket = chip.key === 'dream' || chip.key === 'target' || chip.key === 'safety';
                      const isActive = listFilter === chip.key;
                      return (
                        <button
                          key={String(chip.key)}
                          aria-label={isHeartBucket ? chip.label : undefined}
                          onClick={() => {
                            setListFilter(chip.key);
                            setSelectedSchools(new Set());
                            setSelectedCoaches(new Set());
                          }}
                          style={{
                            padding: '6px 14px',
                            borderRadius: '20px',
                            border: `1.5px solid ${isActive ? chip.color : '#E5E7EB'}`,
                            background: isActive ? `${chip.color}15` : 'white',
                            color: isActive ? chip.color : '#6B7280',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            transition: 'all 0.15s',
                          }}
                        >
                          {typeof chip.key === 'number' && (
                            <Tag size={12} color={isActive ? chip.color : '#9CA3AF'} />
                          )}
                          {isHeartBucket
                            ? renderHeartCategoryPill(chip.key as 'dream' | 'target' | 'safety')
                            : chip.label}
                        </button>
                      );
                    })}
                  </div>
                )}

                {isLoadingCustomSchools && (
                  <div style={{ textAlign: 'center', padding: '12px', color: '#6B7280', fontSize: '14px' }}>
                    Loading custom list schools...
                  </div>
                )}

                {!paidPath && statusLoad === 'loading' && (
                  <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#6B7280' }}>
                    Checking your free programs...
                  </p>
                )}
                {!paidPath && statusLoad === 'error' && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <p role="alert" style={{ margin: 0, flex: '1 1 220px', fontSize: '14px', color: '#374151' }}>
                      {PROGRAMS_STATUS_FAILED}
                    </p>
                    <button
                      type="button"
                      onClick={() => setStatusKey(k => k + 1)}
                      style={{
                        padding: '8px 14px', background: 'white', color: '#374151',
                        border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px',
                        fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      Try again
                    </button>
                  </div>
                )}
                {!paidPath && programState && statusLoad === 'ready' && (
                  outOfPrograms && hasNewSchool ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                      <p style={{ margin: 0, flex: '1 1 220px', fontSize: '14px', color: '#374151' }}>
                        You've used your 3 free programs. Upgrade to reach coaches at more schools.
                      </p>
                      <button
                        type="button"
                        onClick={() => showUpgrade()}
                        style={{
                          padding: '8px 14px', background: '#F9550D', color: 'white', border: 'none',
                          borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                        }}
                      >
                        See plans
                      </button>
                    </div>
                  ) : !outOfPrograms ? (
                    <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#374151' }}>
                      {programState.remaining === 1 ? '1 free program left.' : `${programState.remaining} free programs left.`}
                      {' '}Each new school uses one. Every coach there stays unlocked.
                    </p>
                  ) : null
                )}
                {programError && (
                  <p role="alert" style={{ margin: '0 0 16px', fontSize: '14px', color: '#DC2626' }}>
                    {programError}
                  </p>
                )}
                {pickerSchools.length === 0 && !isLoadingCustomSchools && (paidPath || statusLoad !== 'loading') && (
                  <p style={{ margin: 0, fontSize: '14px', color: '#6B7280' }}>
                    {messagingType === 'tweet'
                      ? 'No schools here have a coach on X yet.'
                      : 'No schools here have coach emails on file yet.'}
                  </p>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {getFilteredSchools().map(school => {
                    const availableCoaches = channel ? offeredCoaches(withStaff(school), channel, paidPath) : [];
                    const isSelected = selectedSchools.has(school.school_id);
                    const note = !paidPath && programState
                      ? rowNote(school.school_id, isSelected, canPickMore, programState)
                      : null;

                    return (
                      <button
                        key={school.school_id}
                        onClick={() => toggleSchoolSelection(school.school_id)}
                        style={{
                          background: isSelected ? 'rgba(249, 85, 13, 0.05)' : 'white',
                          padding: '16px',
                          borderRadius: '12px',
                          border: `2px solid ${isSelected ? '#F9550D' : '#E5E7EB'}`,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.borderColor = '#D1D5DB';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.borderColor = '#E5E7EB';
                          }
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                          {school.logo_url ? (
                            <img
                              src={school.logo_url}
                              alt={school.name}
                              style={{
                                width: '32px',
                                height: '32px',
                                objectFit: 'contain',
                                borderRadius: '6px',
                              }}
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                          ) : (
                            <div
                              style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '6px',
                                background: '#F3F4F6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                <polyline points="21 15 16 10 5 21"></polyline>
                              </svg>
                            </div>
                          )}
                          <div style={{ flex: 1, textAlign: 'left' }}>
                            <div style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937' }}>
                              {school.name}
                            </div>
                            <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '2px' }}>
                              {availableCoaches.length} coach{availableCoaches.length !== 1 ? 'es' : ''} with {messagingType === 'email' ? 'email' : 'X'}
                            </div>
                            {note && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 600, color: note.locked ? '#6B7280' : '#4B5563', marginTop: '4px' }}>
                                {note.locked && <Lock size={12} aria-hidden />}
                                {note.text}
                              </div>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          {renderHeartCategoryPill(school.category)}
                          {/* Custom list tags */}
                          {(schoolMemberships[school.school_id] || []).map(listId => {
                            const cl = customLists.find(l => l.id === listId);
                            if (!cl) return null;
                            return (
                              <span key={listId} style={{
                                display: 'inline-flex', alignItems: 'center', gap: '3px',
                                padding: '2px 8px', borderRadius: '10px', fontSize: '11px',
                                fontWeight: '600', background: `${cl.color}18`, color: cl.color,
                              }}>
                                <Tag size={10} color={cl.color} />
                                {cl.name}
                              </span>
                            );
                          })}
                          {isSelected ? (
                            <CheckCircle size={24} color="#F9550D" />
                          ) : (
                            <Circle size={24} color="#9CA3AF" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Coaches Selection Step */}
            {currentStep === 'coaches' && (
              <div>
                <h3
                  style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#1F2937',
                    marginBottom: '20px',
                  }}
                >
                  Select coaches ({selectedCoaches.size} selected):
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  {getSelectedSchoolsList().map(school => {
                    const availableCoaches = getFilteredCoaches(school);
                    return (
                      <div key={school.school_id}>
                        <h4
                          style={{
                            fontSize: '15px',
                            fontWeight: '600',
                            color: '#374151',
                            marginBottom: '12px',
                          }}
                        >
                          {school.name}
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {availableCoaches.map(coach => {
                            const coachKey = `${school.school_id}-${coach.coach_name}`;
                            const isSelected = selectedCoaches.has(coachKey);
                            
                            return (
                              <button
                                key={coachKey}
                                onClick={() => toggleCoachSelection(school.school_id, coach.coach_name)}
                                style={{
                                  background: isSelected ? 'rgba(249, 85, 13, 0.05)' : 'white',
                                  padding: '12px',
                                  borderRadius: '8px',
                                  border: `1px solid ${isSelected ? '#F9550D' : '#E5E7EB'}`,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  transition: 'all 0.2s',
                                }}
                                onMouseEnter={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.borderColor = '#D1D5DB';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.borderColor = '#E5E7EB';
                                  }
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                                  {school.logo_url ? (
                                    <img
                                      src={school.logo_url}
                                      alt={school.name}
                                      style={{
                                        width: '24px',
                                        height: '24px',
                                        objectFit: 'contain',
                                        borderRadius: '4px',
                                      }}
                                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                    />
                                  ) : (
                                    <div
                                      style={{
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '4px',
                                        background: '#F3F4F6',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
                                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                        <polyline points="21 15 16 10 5 21"></polyline>
                                      </svg>
                                    </div>
                                  )}
                                  <div style={{ flex: 1, textAlign: 'left' }}>
                                    <div style={{ fontSize: '15px', fontWeight: '600', color: '#1F2937' }}>
                                      {coach.coach_name}
                                    </div>
                                    {coach.title && (
                                      <div style={{ fontSize: '13px', color: '#6B7280' }}>
                                        {coach.title}
                                      </div>
                                    )}
                                    <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '2px' }}>
                                      {messagingType === 'email' && coach.email}
                                      {messagingType === 'tweet' && coach.twitter_handle && (
                                        coach.twitter_handle.startsWith('@') ? coach.twitter_handle : `@${coach.twitter_handle}`
                                      )}
                                    </div>
                                  </div>
                                </div>
                                {isSelected ? (
                                  <CheckCircle size={20} color="#F9550D" />
                                ) : (
                                  <Circle size={20} color="#9CA3AF" />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          {(currentStep === 'schools' || currentStep === 'coaches') && (
            <div
              style={{
                padding: '20px 32px',
                borderTop: '1px solid #E5E7EB',
                display: 'flex',
                gap: '12px',
                flexShrink: 0,
                background: 'white',
              }}
            >
              <button
                onClick={() => {
                  if (gating) return;
                  setProgramError(null);
                  setCurrentStep(currentStep === 'coaches' ? 'schools' : 'type');
                }}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  background: 'white',
                  color: '#374151',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#F3F4F6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                }}
              >
                Back
              </button>
              
              <button
                onClick={handleProceed}
                disabled={!canProceed() || gating}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  background: canProceed() && !gating ? '#F9550D' : '#E5E7EB',
                  color: canProceed() && !gating ? 'white' : '#9CA3AF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: canProceed() && !gating ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (canProceed() && !gating) {
                    e.currentTarget.style.background = '#E04A0B';
                  }
                }}
                onMouseLeave={(e) => {
                  if (canProceed() && !gating) {
                    e.currentTarget.style.background = '#F9550D';
                  }
                }}
              >
                {gating
                  ? 'Checking…'
                  : currentStep === 'schools'
                  ? 'Select Coaches'
                  : 'Compose Messages'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Child Modals */}
      <BulkEmailCoachModal
        isOpen={showBulkEmailModal}
        onClose={handleBulkEmailClose}
        onSent={handleClose}
        coaches={getSelectedCoachesData(false)
          .filter((c): c is typeof c & { email: string } => !!c.email)
          .map(c => ({ ...c, email: c.email }))}
        athleteName={athleteName}
        athleteProfileId={athleteId || undefined}
        onRemoveCoach={handleRemoveCoach}
      />

      <BulkTweetCoachModal
        isOpen={showBulkTweetModal}
        onClose={handleBulkTweetClose}
        onPosted={handleClose}
        coaches={getSelectedCoachesData(true).map(coach => ({
          coach_name: coach.coach_name,
          twitter_handle: coach.twitter_handle || '@unknown',
          title: coach.title,
          school: coach.school,
          schoolLogo: coach.schoolLogo,
          coach_id: coach.coach_id,
          school_id: coach.school_id
        }))}
        onRemoveCoach={(twitterHandle) => {
          const coachToRemove = getSelectedCoachesData(true).find(c => c.twitter_handle === twitterHandle);
          if (coachToRemove && coachToRemove.email) {
            handleRemoveCoach(coachToRemove.email);
          }
        }}
      />
      <SpendConfirm />
    </>
  );
};

export default BulkMessagingModal;
